import { shallowRef } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import {
  clientHypotenuse,
  getAverageTouchClient,
  getClientFromFirstTouch,
  getFingerDistance,
  invokeCallback,
  addEventListenerChunk,
  updateEvent,
  checkForTouchDevice,
} from '~/modules/image-zoomer/utils/gesturesUtils';

/** @typedef {import('../types').CustomZoomEvent} CustomZoomEvent */

/**
 *
 * @param {import('vue').Ref<HTMLElement>} element
 * @param {object} callbackMap
 * @param {(event: TouchEvent) => void} callbackMap.pinchStart
 * @param {(event: CustomZoomEvent) => void} callbackMap.pinch
 * @param {(event: CustomZoomEvent) => void} callbackMap.pinchEnd
 * @param {(event: CustomZoomEvent) => void} callbackMap.pan
 * @param {(event: CustomZoomEvent) => void} callbackMap.panEnd
 * @param {(event: Event | TouchEvent) => void} callbackMap.tapStart
 * @param {(event: MouseEvent | TouchEvent) => void} callbackMap.tap
 * @param {(event: MouseEvent) => void} callbackMap.mouseMove
 * @param {(event: KeyboardEvent) => void} callbackMap.onEnter
 * @param {(event: KeyboardEvent) => void} callbackMap.onArrowPressed
 * @param {(event: MouseEvent) => void} [callbackMap.hoverEnd]
 * @returns {{
 *   start: () => {}
 *   stopListening: () => {}
 * }}
 */
export function useGestureListeners(element, callbackMap, { isTopLevelZoomerClick }) {
  const eventData = {
    multifinger: false,
    quickTap: false,
    quickTapTimeout: null,
    startEvent: null,
    lastMoveEvent: null,
    lastPinchEvent: null,
    startDist: null,
  };
  // Used to automatically clean up all attached event listeners at once
  const mouseEventController = new AbortController();
  const { signal: mouseEventSignal } = mouseEventController;
  // Explicitly manages cleanup for listeners attached to the document body
  const bodyMousemoveTearDown = shallowRef(null);
  /**
   *
   * @type {(event: MouseEvent | TouchEvent) => void}
   */
  const startEvent = (event) => {
    if (isTopLevelZoomerClick && !event.target.classList.contains('picture-image')) return;

    clearTimeout(eventData.quickTapTimeout);

    if (event.type === 'touchstart') {
      updateEvent(event, getClientFromFirstTouch(event));
      if (event.touches.length > 1) {
        eventData.multifinger = true;

        if (event.touches.length === 2) {
          invokeCallback(event, callbackMap.pinchStart);
        }

        return;
      }
    }

    eventData.quickTap = true;
    eventData.quickTapTimeout = setTimeout(() => {
      eventData.quickTap = false;
    }, 300);

    eventData.startEvent = event;
    eventData.lastMoveEvent = null;
    eventData.lastPinchEvent = null;

    invokeCallback(event, callbackMap.tapStart);
  };

  const moveEvent = useThrottleFn((event) => {
    if (!isTopLevelZoomerClick) {
      event.preventDefault();
    }
    const isLeftMouseDown = event.buttons === 1;

    if (event.type === 'mousemove' && !isLeftMouseDown) {
      return;
    }

    if (event.type === 'touchmove') {
      updateEvent(event, {
        movementX: event.changedTouches[0]?.screenX - eventData.lastMoveEvent?.changedTouches[0]?.screenX || 0,
        movementY: event.changedTouches[0]?.screenY - eventData.lastMoveEvent?.changedTouches[0]?.screenY || 0,
        ...getAverageTouchClient(event),
      });

      if (event.touches.length > 1) {
        if (event.touches.length === 2) {
          const fingerDistance = getFingerDistance(event);

          eventData.startDist = eventData.startDist || fingerDistance;

          updateEvent(event, {
            fingerDistance,
            deltaScale: fingerDistance / eventData.startDist,
            movementFingerDistance: fingerDistance - eventData.lastPinchEvent?.fingerDistance || 0,
          });

          invokeCallback(event, callbackMap.pinch);
        }

        eventData.lastPinchEvent = event;

        if (event.changedTouches.length === 1) {
          return;
        }
      }
    }

    if (eventData.lastMoveEvent) {
      updateEvent(event, {
        deltaX: event.clientX - eventData.lastMoveEvent.clientX,
        deltaY: event.clientY - eventData.lastMoveEvent.clientY,
      });
    }

    const { lastMoveEvent } = eventData;

    eventData.lastMoveEvent = event;

    if (event.type === 'touchmove' && (event.touches.length > 1 || lastMoveEvent?.changedTouches?.length !== event?.changedTouches?.length)) {
      return;
    }

    invokeCallback(event, callbackMap.pan);
  }, 1000 / 120);

  /**
   *
   * @type {(event: MouseEvent | TouchEvent) => void}
   */
  const endEvent = (event) => {
    if (isTopLevelZoomerClick && !event.target.classList.contains('picture-image')) return;
    if (eventData.multifinger) {
      if (!event.touches.length) {
        eventData.multifinger = false;
      } else if (event.touches.length < 2) {
        const timeDifference = event.timeStamp - eventData.lastPinchEvent.timeStamp;
        updateEvent(event, {
          velFingers: eventData.lastPinchEvent.movementFingerDistance / timeDifference,
        });

        invokeCallback(event, callbackMap.pinchEnd);
      }
      eventData.startDist = null;

      return;
    }

    if (eventData.lastMoveEvent) {
      const timeDifference = event.timeStamp - eventData.lastMoveEvent.timeStamp;

      updateEvent(event, {
        velX: eventData.lastMoveEvent.movementX / timeDifference,
        velY: eventData.lastMoveEvent.movementY / timeDifference,
      });

      invokeCallback(event, callbackMap.panEnd);
    }

    if (event.type === 'touchend') {
      const rectangle = event.target?.getBoundingClientRect();
      const scale = rectangle?.width / event.target?.width || 1;
      updateEvent(event, {
        offsetX: (event.changedTouches[0]?.pageX - rectangle?.left) / scale,
        offsetY: (event.changedTouches[0]?.pageY - rectangle?.top) / scale,
        ...getClientFromFirstTouch(event),
      });
    }

    const pointerTravelDistance = clientHypotenuse(eventData.startEvent, event);
    const POINTER_TRAVEL_LIMIT = 20;
    if (!eventData.quickTap || pointerTravelDistance > POINTER_TRAVEL_LIMIT) {
      return;
    }

    invokeCallback(event, callbackMap.tap);

    eventData.quickTap = false;
  };

  /**
   *
   * @type {(event: KeyboardEvent) => void}
   */
  const keyboardEvent = (event) => {
    const keyName = event.key;

    const callback = {
      Enter: callbackMap.onEnter,
      ArrowDown: callbackMap.onArrowPressed,
      ArrowUp: callbackMap.onArrowPressed,
      ArrowLeft: callbackMap.onArrowPressed,
      ArrowRight: callbackMap.onArrowPressed,
    }[keyName];

    if (callback) {
      invokeCallback(event, callback);
    }
  };

  let listenerCallbacks = [startEvent, moveEvent, endEvent, keyboardEvent];

  if (isTopLevelZoomerClick) {
    listenerCallbacks = [...listenerCallbacks, callbackMap.hoverEnd];
  }

  /**
   * Attaches a mousemove listener to the document body.
   * @param {(event: MouseEvent) => void} callback - The function to execute on mouse move.
   * @returns {() => void} A teardown function to remove the body listener.
   */
  const addBodyMousemoveListener = (callback) => {
    const bodyMousemoveCallback = (event) => {
      invokeCallback(event, callback);
    };
    document?.body.addEventListener('mousemove', bodyMousemoveCallback);

    return () => {
      document?.body.removeEventListener('mousemove', bodyMousemoveCallback);
    };
  };

  /**
   * Starts listening to gesture events.
   * @param {boolean} [bodyOnly=false] - If true, only attaches the document body listener (skips element listeners).
   */
  const start = (bodyOnly = false) => {
    const isTouchDevice = checkForTouchDevice();
    if (!bodyOnly) {
      addEventListenerChunk(element, listenerCallbacks, isTouchDevice, isTopLevelZoomerClick, { signal: mouseEventSignal });
    }

    if (!isTouchDevice) {
      bodyMousemoveTearDown.value = addBodyMousemoveListener(callbackMap.mouseMove);
    }
  };

  /**
   * Stops listening to gesture events.
   * @param {boolean} [bodyOnly=false] - If true, only removes the document body listener (skips element listeners cleanup via AbortController).
   */
  const stopListening = (bodyOnly = false) => {
    if (!bodyOnly) {
      mouseEventController.abort();
    }
    bodyMousemoveTearDown.value?.();
    bodyMousemoveTearDown.value = null;
  };

  return {
    start,
    stopListening,
  };
}

export default useGestureListeners;
